import { useState } from 'react';
import { RefreshCw, CheckCircle, BookOpen, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useDecisionsStore } from '../../../stores/prioritization/decisionsStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { usePermissions } from '../../../hooks/prioritization/usePermissions';
import { UI_LABELS } from '../../../lib/terminology';
import { RationaleFields } from '../decisions/RationaleFields';
import { EMPTY_RATIONALE, type RationaleValue } from '../../../lib/rationaleParts';

export function Realignment() {
  const navigate = useNavigate();
  const itemPools = useItemPoolsStore((state) => state.itemPools);
  const setActiveItemPool = useItemPoolsStore((state) => state.setActiveItemPool);
  const driftEvents = useItemPoolsStore((state) => state.driftEvents);
  const addNotification = useUIStore((state) => state.addNotification);
  const createDecision = useDecisionsStore((state) => state.createDecision);
  const permissions = usePermissions();

  // Per-list rationale capture state
  const [openRationale, setOpenRationale] = useState<string | null>(null);
  const [rationaleValues, setRationaleValues] = useState<Record<string, RationaleValue>>({});
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [recordedIds, setRecordedIds] = useState<Set<string>>(new Set());

  const listsNeedingRealignment = itemPools.filter(list => {
    const hasUnresolvedDrift = driftEvents.some(e => e.itemPoolId === list.id && !e.resolved);
    const lastRankedDate = list.lastRankedAt ? new Date(list.lastRankedAt) : new Date(list.createdAt);
    const daysSinceRanking = (Date.now() - lastRankedDate.getTime()) / (1000 * 60 * 60 * 24);
    const stale = daysSinceRanking > 14;
    return hasUnresolvedDrift || stale;
  });

  const handleStartRealignment = (listId: string) => {
    setActiveItemPool(listId);
    navigate('/prioritization/lists');
    addNotification({
      type: 'info',
      title: 'Realignment Started',
      message: `Opening ${UI_LABELS.itemPool.toLowerCase()} for re-ranking`,
      duration: 2000
    });
  };

  const handleRecordDecision = async (list: (typeof itemPools)[0]) => {
    const rationale = rationaleValues[list.id] ?? EMPTY_RATIONALE;
    setRecordingId(list.id);
    const saved = await createDecision({
      title: `Realignment: ${list.name}`,
      decisionKind: 'manual',
      decisionClass: 'review',
      impact: 'high',
      itemPoolId: list.id,
      source: 'realignment',
      tags: ['realignment'],
      rationaleContext: rationale.rationaleContext || undefined,
      rationaleReasoning: rationale.rationaleReasoning || undefined,
      rationaleAlternatives: rationale.rationaleAlternatives || undefined,
      reconsiderationConditions: rationale.reconsiderationConditions || undefined,
      reviewByDate: rationale.reviewByDate || undefined,
    });
    setRecordingId(null);
    if (!saved) {
      // Keep the rationale open with what was typed; do not show "recorded".
      addNotification({
        type: 'error',
        title: 'Decision was not recorded',
        message: useDecisionsStore.getState().lastError ?? 'Try again.',
        duration: 7000,
      });
      return;
    }
    setRecordedIds((prev) => new Set([...prev, list.id]));
    setOpenRationale(null);
    // Auto-clear the success state after 3 seconds
    setTimeout(() => {
      setRecordedIds((prev) => {
        const next = new Set(prev);
        next.delete(list.id);
        return next;
      });
    }, 3000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <RefreshCw className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">Focus Realignment</h1>
        </div>
        <p className="text-[var(--text-sec)]">
          Respond to shifting conditions with structured re-ranking and adjustment
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-[var(--text-sec)]">Total Lists</p>
          <p className="text-3xl font-bold text-[var(--text-pri)]">{itemPools.length}</p>
        </div>
        <div className="card border-[var(--mustard)] bg-[var(--mustard-dim)]">
          <p className="text-sm text-[var(--mustard)] font-medium">Need Realignment</p>
          <p className="text-3xl font-bold text-[var(--mustard)]">{listsNeedingRealignment.length}</p>
        </div>
        <div className="card border-[var(--green)] bg-[var(--green-dim)]">
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">Up to Date</p>
          <p className="text-3xl font-bold text-[var(--green)] dark:text-[var(--text-ter)]">
            {itemPools.length - listsNeedingRealignment.length}
          </p>
        </div>
      </div>

      {/* Lists Needing Realignment */}
      {listsNeedingRealignment.length > 0 ? (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--text-pri)]">
              Lists Needing Realignment ({listsNeedingRealignment.length})
            </h2>
          </div>

          <div className="space-y-3">
            {listsNeedingRealignment.map((list) => {
              const listDrift = driftEvents.filter(e => e.itemPoolId === list.id && !e.resolved);
              const lastRankedDate = list.lastRankedAt ? new Date(list.lastRankedAt) : new Date(list.createdAt);
              const daysSinceRanking = Math.floor((Date.now() - lastRankedDate.getTime()) / (1000 * 60 * 60 * 24));
              const isStale = daysSinceRanking > 14;
              const rationaleOpen = openRationale === list.id;
              const recorded = recordedIds.has(list.id);

              return (
                <div
                  key={list.id}
                  className="border border-[var(--mustard)] bg-[var(--mustard-dim)] overflow-hidden"
                >
                  {/* Main row */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-[var(--text-pri)]">{list.name}</h3>

                        {list.description && (
                          <p className="text-sm text-[var(--text-sec)] mt-1">{list.description}</p>
                        )}

                        <div className="mt-3 space-y-2">
                          {listDrift.length > 0 && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="px-2 py-1 bg-[var(--coral-dim)] text-[var(--coral)] rounded text-xs font-medium">
                                {listDrift.length} Drift Event{listDrift.length > 1 ? 's' : ''}
                              </span>
                              <span className="text-[var(--text-sec)]">Unresolved misalignments detected</span>
                            </div>
                          )}

                          {isStale && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="px-2 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded text-xs font-medium">
                                Stale
                              </span>
                              <span className="text-[var(--text-sec)]">Last ranked {daysSinceRanking} days ago</span>
                            </div>
                          )}

                          <div className="flex items-center gap-4 text-xs text-[var(--text-sec)] mt-2">
                            <span>{list.priorityIds.length} priorities</span>
                            <span>{list.cutLinePosition} above the line</span>
                          </div>
                        </div>
                      </div>

                      <div className="ml-4 flex items-center gap-2">
                        {recorded ? (
                          <span className="flex items-center gap-1 text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                            <CheckCircle className="h-4 w-4" />
                            Recorded
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setOpenRationale(rationaleOpen ? null : list.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
                          >
                            <BookOpen className="h-4 w-4" />
                            {rationaleOpen ? 'Hide Rationale' : 'Document Context'}
                          </button>
                        )}

                        {(permissions.currentRole === 'list-owner' || permissions.canFacilitateSessions) && (
                          <Link
                            to="/prioritization/lists"
                            onClick={() => handleStartRealignment(list.id)}
                            className="btn btn-primary text-sm flex items-center gap-2"
                          >
                            <RefreshCw className="h-4 w-4" />
                            Realign
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Rationale capture panel */}
                  {rationaleOpen && (
                    <div className="border-t border-[var(--mustard)] bg-[var(--bg-raised)] p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-[var(--text-sec)]">
                          Document the context driving this realignment
                        </p>
                        <button
                          type="button"
                          onClick={() => setOpenRationale(null)}
                          className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      <RationaleFields
                        value={rationaleValues[list.id] ?? EMPTY_RATIONALE}
                        onChange={(val) =>
                          setRationaleValues((prev) => ({ ...prev, [list.id]: val }))
                        }
                        title="Why this realignment"
                        defaultOpen
                        disabled={recordingId === list.id}
                      />

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRecordDecision(list)}
                          disabled={recordingId === list.id}
                          className="btn btn-primary text-sm"
                        >
                          {recordingId === list.id ? 'Recording…' : 'Record Decision'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="card border-[var(--green)] bg-[var(--green-dim)] text-center py-12">
          <CheckCircle className="h-12 w-12 mx-auto mb-3 text-[var(--green)]" />
          <p className="text-[var(--green)] dark:text-[var(--text-ter)] font-medium">All lists are up to date</p>
          <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] mt-1">No realignment needed at this time</p>
        </div>
      )}

      {/* Realignment Process Guide */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <h3 className="font-medium text-[var(--text-sec)] mb-3">Realignment Process</h3>
        <div className="space-y-3">
          {[
            { n: 1, title: 'Review Drift Events', body: 'Understand what changed and why alignment was lost' },
            { n: 2, title: 'Document Context', body: 'Record the rationale driving this realignment decision' },
            { n: 3, title: 'Re-rank Priorities', body: 'Adjust the order based on current context and constraints' },
            { n: 4, title: 'Adjust Cut Line', body: 'Revise what\'s above and below based on capacity' },
            { n: 5, title: 'Communicate Changes', body: 'Update stakeholders on new priorities and rationale' },
          ].map(({ n, title, body }) => (
            <div key={n} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--slate)] text-white flex items-center justify-center text-sm font-bold">
                {n}
              </div>
              <div>
                <p className="text-sm font-medium text-[var(--text-sec)]">{title}</p>
                <p className="text-xs text-[var(--text-sec)]">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reprioritization Triggers */}
      <div className="card">
        <h3 className="font-medium text-[var(--text-pri)] mb-3">Common Reprioritization Triggers</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { title: 'Resource Changes', body: 'Team size, budget, or capacity shifts' },
            { title: 'Strategic Pivots', body: 'New market opportunities or competitive threats' },
            { title: 'Deadline Changes', body: 'External deadlines moved or new time constraints' },
            { title: 'Stakeholder Feedback', body: 'New insights from customers or leadership' },
          ].map(({ title, body }) => (
            <div key={title} className="p-3 bg-[var(--surface)] border border-[var(--rule)]">
              <p className="text-sm font-medium text-[var(--text-pri)]">{title}</p>
              <p className="text-xs text-[var(--text-sec)] mt-1">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
